import { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, Info, ChevronRight, Loader2, ShieldCheck, Stethoscope, CheckCircle2, XCircle, AlertOctagon } from 'lucide-react';
import axios from 'axios';

const API = '';

const SEV = {
  critical: { icon: AlertCircle, bar: 'bg-red-500', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', label: 'Critical' },
  warning: { icon: AlertTriangle, bar: 'bg-amber-500', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Warning' },
  info: { icon: Info, bar: 'bg-teal-500', bg: 'bg-teal-50 border-teal-200', badge: 'bg-teal-100 text-teal-700', label: 'Info' },
};

const CHECK_STATUS = {
  pass: { icon: CheckCircle2, cls: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  warn: { icon: AlertTriangle, cls: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  fail: { icon: XCircle, cls: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

function DiagnosisView({ diagnosis }) {
  if (!diagnosis) return null;
  const verdictColor = diagnosis.verdict === 'healthy' ? 'text-green-700 bg-green-50 border-green-200' :
    diagnosis.verdict === 'critical' ? 'text-red-700 bg-red-50 border-red-200' : 'text-amber-700 bg-amber-50 border-amber-200';

  return (
    <div className="mt-3 space-y-2" data-testid="diagnosis-results">
      <div className={`rounded-lg border p-3 ${verdictColor}`}>
        <p className="text-xs font-semibold">{diagnosis.verdict_text}</p>
        {diagnosis.priority_action && (
          <p className="text-[11px] mt-1 opacity-80">{diagnosis.priority_action}</p>
        )}
      </div>
      <div className="space-y-1.5">
        {diagnosis.checks.map((check, i) => {
          const st = CHECK_STATUS[check.status] || CHECK_STATUS.warn;
          const Icon = st.icon;
          return (
            <div key={i} className={`rounded-lg border p-2.5 ${st.bg}`}>
              <div className="flex items-center gap-2">
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${st.cls}`} />
                <span className="text-[11px] font-semibold">{check.name}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed ml-5.5">{check.detail}</p>
              {check.action && (
                <div className="flex items-start gap-1 mt-1.5 ml-5.5">
                  <ChevronRight className="w-3 h-3 flex-shrink-0 mt-0.5 text-primary" />
                  <p className="text-[10px] text-primary font-medium">{check.action}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlertCard({ alert, token }) {
  const [expanded, setExpanded] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const s = SEV[alert.severity] || SEV.info;
  const Icon = s.icon;
  const isCampaignAlert = alert.category === 'campaign' && alert.title.includes(':');

  const handleDiagnose = async (e) => {
    e.stopPropagation();
    if (diagnosis) { setDiagnosis(null); return; }
    setDiagnosing(true);
    try {
      const campaignName = alert.title.split(':').pop().trim();
      const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };
      const { data } = await axios.get(`${API}/api/admin/ads/diagnose/${encodeURIComponent(campaignName)}`, opts);
      setDiagnosis(data.diagnosis);
      if (!expanded) setExpanded(true);
    } catch { /* */ }
    setDiagnosing(false);
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${s.bg}`}
      onClick={() => setExpanded(!expanded)}
      data-testid={`alert-${alert.category}-${alert.severity}`}
    >
      <div className="flex items-start gap-3 p-3">
        <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${s.bar}`} />
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-70" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${s.badge}`}>{s.label}</span>
            <p className="text-xs font-semibold truncate">{alert.title}</p>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{alert.message}</p>
          {expanded && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap gap-2">
                {Object.entries(alert.metrics || {}).map(([k, v]) => (
                  <span key={k} className="text-[10px] px-2 py-1 rounded bg-white/60 border border-black/5 font-medium">
                    {k.replace(/_/g, ' ')}: <strong>{v}</strong>
                  </span>
                ))}
              </div>
              <div className="flex items-start gap-1.5 text-[11px] text-primary font-medium">
                <ChevronRight className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>{alert.action}</span>
              </div>
              {isCampaignAlert && (
                <button
                  onClick={handleDiagnose}
                  disabled={diagnosing}
                  className="mt-1 px-3 py-1.5 text-[10px] font-medium bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="diagnose-btn"
                >
                  {diagnosing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
                  {diagnosis ? 'Hide diagnosis' : 'Diagnose this campaign'}
                </button>
              )}
              {diagnosis && <DiagnosisView diagnosis={diagnosis} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AlertsPanel({ token }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };
    axios.get(`${API}/api/admin/alerts`, opts)
      .then(({ data }) => setAlerts(data.alerts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-border p-5 shadow-sm" data-testid="alerts-panel">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Scanning KPIs...
        </div>
      </div>
    );
  }

  const critical = alerts.filter(a => a.severity === 'critical');
  const warning = alerts.filter(a => a.severity === 'warning');
  const info = alerts.filter(a => a.severity === 'info');

  return (
    <div className="bg-white rounded-xl border border-border p-5 shadow-sm" data-testid="alerts-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">KPI Alert Bot</h3>
        </div>
        {alerts.length > 0 && (
          <div className="flex items-center gap-2 text-[10px]">
            {critical.length > 0 && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{critical.length} critical</span>}
            {warning.length > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">{warning.length} warning</span>}
            {info.length > 0 && <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-medium">{info.length} info</span>}
          </div>
        )}
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-6" data-testid="alerts-empty">
          <ShieldCheck className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">No alerts yet — not enough live data to analyze.</p>
          <p className="text-[10px] text-muted-foreground mt-1">Alerts will appear here once campaigns are running and funnel events are tracked.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert, i) => (
            <AlertCard key={`${alert.category}-${alert.severity}-${i}`} alert={alert} token={token} />
          ))}
        </div>
      )}
    </div>
  );
}
